import axios from "axios";
import { NGROK } from "../../../../APIs";

export interface TenantData {
  tenantName: string;
  [key: string]: any;
}

// Azure subscription type from API
export interface AzureSubscription {
  subscriptionName: string;
  azureSubscriptionId: string;
  [key: string]: any;
}

// Fetch all tenants
export const fetchTenants = async (): Promise<TenantData[]> => {
  const response = await axios.get(`${NGROK}/api/get-all-domains`, {
  });
  return response.data || [];
};

// Fetch GCP project IDs
export const fetchGcpProjectIds = async (tenantName: string): Promise<string[]> => {
  const response = await axios.get(`${NGROK}/api/gcp/auth/fetchAllProjectIds`, {
    params: {
      tenantName: tenantName,
    },
  });
  return response.data || [];
};

// AWS Account type from API
export interface AwsAccount {
  id: number;
  accountId: string;
  accountName: string;
  email: string;
  organizationId: string;
  tenantName: string;
  status: string;
  accountType: string;
  joinedMethod: string;
  joinedTimestamp: string;
  roleArn: string;
}

// Fetch AWS accounts
export const fetchAwsAccounts = async (tenantName: string): Promise<AwsAccount[]> => {
  const response = await axios.get(`${NGROK}/api/aws/getAllAccounts`, {
    params: {
      tenantName: tenantName,
    },
  });
  return response.data || [];
};

// Fetch Azure subscriptions
export const fetchAzureSubscriptions = async (tenantName: string): Promise<AzureSubscription[]> => {
  const response = await axios.get(`${NGROK}/api/azureResources/v1/all`, {
    params: {
      tenantName: tenantName,
      type: "SUBSCRIPTION",
    },
  });
  return response.data || [];
};

// CloudAlert from API response
export interface CloudAlertResponse {
  id: number;
  alertId: string;
  title: string;
  severity: string;
  alertType: string;
  info: Record<string, string>;
  steps: Record<number, string>;
  mitre: Record<string, string>;
  isFlagged: boolean;
  isDefault: boolean;
  cloudType: string;
  tenantName: string;
  cloudAccountId: string;
  users?: string[];
  resource?: string[];
  timeWindow?: string[];
}

// Grouped alerts response type
export type GroupedAlertsResponse = {
  behaviour: {
    critical: CloudAlertResponse[];
    high: CloudAlertResponse[];
    medium: CloudAlertResponse[];
  };
  rule: {
    critical: CloudAlertResponse[];
    high: CloudAlertResponse[];
    medium: CloudAlertResponse[];
  };
  sequential: {
    critical: CloudAlertResponse[];
    high: CloudAlertResponse[];
    medium: CloudAlertResponse[];
  };
};

// Fetch alerts for a cloud resource
export const fetchAlerts = async (
  tenantName: string,
  cloudId: string,
  cloudType: string,
): Promise<GroupedAlertsResponse> => {
  const response = await axios.get(`${NGROK}/api/cloud/get-alerts`, {
    params: {
      tenantName,
      cloudId,
      cloudType,
    },
  });
  return response.data;
};

// Generate alert API
export interface GenerateAlertRequest {
  tenantName: string;
  cloudId: string;
  cloudType: string;
  userPrompt: string;
  alertType: "AUTOMATIC" | "RULE" | "BEHAVIOUR" | "SEQUENTIAL";
}

export interface GeneratedAlertResponse {
  id: number | null;
  title: string;
  severity: string;
  alertType: string;
  info: {
    why: string;
    what: string;
    how: string;
  };
  steps: Record<string, string>;
  mitre: Record<string, string>;
  isFlaged: boolean;
  users?: string[];
  resource?: string[];
  timeWindow?: string[];
}

export const generateAlert = async (request: GenerateAlertRequest): Promise<GeneratedAlertResponse[]> => {
  const response = await axios.post(`${NGROK}/api/cloud/generate-alert`, request, {
    timeout: 450000, // 3 minute timeout
  });
  return response.data || [];
};

// Switch alert enabled/disabled status
export const switchAlert = async (alertId: string, cloudType: string, tenantName: string, cloudId: string): Promise<boolean> => {
  const response = await axios.get(`${NGROK}/api/cloud/switch-alert`, {
    params: {
      alertId,
      cloudType,
      tenantName,
      cloudId,
    },
  });
  return response.data;
};

// Save alert DTO for API request
export interface CloudAlertDTO {
  title: string;
  severity: string;
  alertType: "BEHAVIOUR" | "RULE" | "SEQUENTIAL";
  info: Record<string, string>;
  steps: Record<number, string>;
  mitre: Record<string, string>;
  isFlagged: boolean;
  isDefault: boolean;
  users?: string[];
  resource?: string[];
  timeWindow?: string[];
}

// Save alert request type
export interface SaveAlertRequest {
  alerts: CloudAlertDTO[];
  cloudType: string;
  tenantName: string;
  cloudAccountId: string;
}

// Save alerts to library
export const saveAlerts = async (request: SaveAlertRequest): Promise<CloudAlertResponse[]> => {
  const response = await axios.post(`${NGROK}/api/cloud/save-alert`, request, {
  });
  return response.data || [];
};

// Switch all alerts enabled/disabled status
export const switchAllAlerts = async (
  tenantName: string,
  cloudId: string,
  cloudType: string,
  isFlagged: boolean,
): Promise<boolean> => {
  const response = await axios.get(`${NGROK}/api/cloud/switch-all-alert`, {
    params: {
      tenantName,
      cloudId,
      cloudType,
      isFlagged,
    },
  });
  return response.data;
};

// Selectable items response type
export interface SelectableItemsResponse {
  users: string[];
  serviceAccounts?: string[];
  serviceAccount?: string[]; // API may return singular form
  awsIamRole?: string[];
  resources: Record<string, string[]>;
}

// Fetch selectable items for alert editing
export const fetchAlertSelectableItems = async (
  tenantName: string,
  cloudId: string,
  cloudType: string,
): Promise<SelectableItemsResponse> => {
  const response = await axios.get(`${NGROK}/api/cloud/get-alert-selectable-items`, {
    params: {
      tenantName,
      cloudId,
      cloudType,
    },
  });
  return response.data;
};

// Update alert request type
export interface UpdateAlertRequest {
  alertId: string;
  users: string[];
  resource: string[];
  timeWindow: string[];
  cloudType: string;
  tenantName: string;
  cloudId: string;
}

// Update alert
export const updateAlert = async (request: UpdateAlertRequest): Promise<CloudAlertResponse> => {
  const response = await axios.post(`${NGROK}/api/cloud/update-alert`, request, {
  });
  return response.data;
};
